import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {throwError, Observable} from 'rxjs';
import {tap, catchError, filter, take} from 'rxjs/operators';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';

import {TokenService} from './token.service';
import {environment} from '@env/environment';
import {State} from '@app/app.reducers';
import {SignOutAction, SignOutClearDataAction} from '@app/views/common/auth.actions';
import moment from 'moment';
import { Moment } from 'moment';
import {LogService} from '../log/log.service';
import {UserActivityService} from '@app/core/global/user-activity.service';
import {SplashScreenService} from '@app/core/service/splash-screen.service';
import {AuthenticationService} from '@app/core/service/authentication.service';
import {isValidURL} from '@app/core/utils/other-utils';
import {isLoginRoute} from '@app/core/main.module.routes';
import {UserStatus} from '@app/core/model/user';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    private isAuthorizationInProgress = false;

    private guidRegExp = `[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}`;
    private trackingUrls = [
        new RegExp(`api\/v2\/rosterAccess\/integrations-without-access-complete\/` + this.guidRegExp),
        new RegExp(`api\/v2\/rosterAccess\/progress\/` + this.guidRegExp + `\/` + this.guidRegExp),
        new RegExp(`api\/v2\/oneedtech\/vendors\/status\/` + this.guidRegExp),
        new RegExp(`api\/v2\/oneedtech\/districts\/status\/` + this.guidRegExp),
        new RegExp(`api\/v2\/directory\/organizations\/delete\/progress`),
        new RegExp(`api\/v2\/dashboard\/data-access-consistency-progress`),
        new RegExp(`api\/v2\/dashboard\/runningProcesses`),
        new RegExp(`api\/v2\/rosterAccess\/access-in-progress`),
        new RegExp(`api\/v2\/applications\/integrations\/stats\\?rowCount=20\&applications=`),
        new RegExp(`api\/v2\/job-queues\/access`),
        new RegExp(`api\/v2\/job-queues\/event`),
        new RegExp(`api\/v2\/job-queues\/sync`),
    ];

    constructor(private tokenService: TokenService,
                private logService: LogService,
                private userActivityService: UserActivityService,
                private authenticationService: AuthenticationService,
                private router: Router,
                private store: Store<State>,
                private matDialog: MatDialog,
                private splashScreen: SplashScreenService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const token = this.tokenService.getToken();
        const isServerRequest = (request.url.indexOf(environment.dataApiBaseURI) === 0)
                             || (request.url.indexOf(environment.datahubHost) === 0);
        const hasAuthorizationHeader = request.headers.has('Authorization');
        const isRequestAuthenticated = hasAuthorizationHeader || request.headers.has('X-Auth-Token');

        if (isServerRequest && token && !isRequestAuthenticated) {
            request = request.clone({
                setHeaders: {
                    'X-Auth-Token': `${token}`
                }
            });
        }

        const isAuthServiceRequest = (request.url.indexOf(environment.etxBaseURI) === 0);
        if (isAuthServiceRequest) {
            request = request.clone({
                setHeaders: {
                    'Access-Control-Allow-Credentials': 'true',
                    'Access-Control-Allow-Origin': window.location.origin,
                    'Access-Control-Allow-Methods': 'OPTIONS, GET, POST'
                },
                withCredentials: true
            });
        }
        const requestBeginTime = moment();
        const logoutUrl = `${environment.datahubHost}/auth/logout`;

        this.authenticationService.checkETXAccessToken();
        const isAuthorizationRequest = (request.url === `${environment.datahubHost}/auth/opaque/token`);

        return next.handle(request)
            .pipe(
                tap(() => {
                    if (isServerRequest
                        && request.url !== logoutUrl
                        && !this.trackingUrls.some(urlRegExp => request.url.match(urlRegExp))
                    ) {
                        this.userActivityService.refreshInactivityTimeout();
                    }
                }),
                catchError(error => {
                    if (error instanceof HttpErrorResponse) {
                        // ToDo: add login page urls
                        const isLoginPage = isLoginRoute(this.router.url);
                        const isLoginRequest = (request.url === `${environment.datahubHost}/auth/login`);
                        const isLogoutRequest = (request.url === logoutUrl);

                        if (!isLoginPage || !isLoginRequest) {
                            const isNotAuthorized = (error.status === 401) && !hasAuthorizationHeader;
                            const isTestingServerStatus = (request.url === `${environment.datahubBaseURI}/actuator/health`);
                            const isServiceUnavailable = (error.status === 503)/* || (error.status === 0)*/;

                            if (isServerRequest && (isNotAuthorized || (isServiceUnavailable && !isTestingServerStatus))) {

                                const lastVisitedPage = this.router.routerState.snapshot.root.queryParams['lastVisitedPage'];
                                const returnUrl = this.router.routerState.snapshot.root.queryParams['returnUrl'];

                                const queryParams = {};
                                queryParams[(lastVisitedPage ? 'lastVisitedPage' : 'returnUrl')] = lastVisitedPage || returnUrl;

                                // if (isServiceUnavailable) {
                                //     this.router.navigate(['/maintenance'], {queryParams: {returnUrl: returnUrl}});
                                // } else {
                                this.splashScreen.setHideOnNavigationEnd(true);
                                this.splashScreen.hide();
                                // const signOutRedirectUrl = (isNotAuthorized && error.error && isValidURL(error.error))
                                //     ? error.error
                                //     : null;
                                // this.authenticationService.redirectToLoginPage({queryParams: queryParams}, signOutRedirectUrl);

                                const isAccountExpired = (isAuthorizationRequest && (error.error === <string>UserStatus.TO_BE_DELETED));
                                if (isNotAuthorized
                                    && !isLogoutRequest
                                    && !isAccountExpired
                                    // && (!this.authenticationService.isCheckETXAccessTokenInProgress$.getValue() || isAuthorizationRequest)
                                    // Use throttle for signOutAction instead of this
                                ) {
                                    this.store.dispatch(new SignOutAction());
                                }
                                // }
                            } else {
                                if (!isServiceUnavailable) {
                                    this.logHttpError(requestBeginTime, request.url, request.method, <HttpErrorResponse>error);
                                }
                            }
                        }
                    }
                    return throwError(error);
                })
            );
    }

    private logHttpError(startMoment: Moment, url: string, method: string, error: HttpErrorResponse) {
        const requestDuration = moment().diff(startMoment, 'milliseconds');

        this.logService.logHttpError(`HTTP ${method} - ${error.message}`, requestDuration, url, error.status);
    }
}
